import { useState } from 'react'
import { salidasApi } from '../api/salidas'
import { fmtMonto } from '../utils/fecha'
import Boton from './Boton'

// Costo de la salida + Andreani en lote (Martin, 3 oct 2026).
// - Costo: lo que cobra el fletero externo por la salida (o lo que cotizaron
//   los envíos por Andreani) y el costo por entrega = costo ÷ documentos.
// - Salida de Andreani: genera todas las órdenes de una vez e imprime todas
//   las etiquetas en un PDF para llevar los paquetes a la sucursal.
export default function SalidaCostoAndreani({ salida, onCambio }) {
  const docs = (salida.documentos || []).filter(d => !d.liberado_at)
  const esAndreani = !!salida.fletero?.es_andreani
  const esExterno = !!salida.fletero?.externo
  const [editando, setEditando] = useState(false)
  const [monto, setMonto] = useState(salida.costo ?? '')
  const [trabajando, setTrabajando] = useState('')
  const [resultado, setResultado] = useState(null)
  const [error, setError] = useState('')

  if (!esAndreani && !esExterno && salida.costo == null) return null

  const conOrden = docs.filter(d => d.entidad?.andreani_agrupador).length
  const sinOrden = docs.length - conOrden
  const costo = salida.costo != null ? Number(salida.costo) : null

  const guardar = async () => {
    setTrabajando('costo'); setError('')
    try {
      await salidasApi.guardarCosto(salida.id, monto === '' ? null : Number(monto))
      setEditando(false); onCambio()
    } catch (e) { setError(e.response?.data?.message || 'No se pudo guardar.') } finally { setTrabajando('') }
  }

  const generar = async () => {
    if (!window.confirm(`¿Generar ${sinOrden} envío(s) en Andreani? Cada orden es real y se paga.`)) return
    setTrabajando('generar'); setError(''); setResultado(null)
    try {
      let data
      try {
        ({ data } = await salidasApi.generarAndreani(salida.id))
      } catch (e) {
        // Bebidas sin contenido (ml): el aviso llega antes de generar nada (sug #99).
        const aviso = e.response?.status === 422 && e.response?.data?.errors?.sin_contenido?.[0]
        if (!aviso) throw e
        if (!window.confirm(aviso)) return
        ;({ data } = await salidasApi.generarAndreani(salida.id, { confirmar_sin_contenido: true }))
      }
      setResultado(data); onCambio()
    } catch (e) { setError(e.response?.data?.message || 'No se pudieron generar los envíos.') } finally { setTrabajando('') }
  }

  const imprimir = async () => {
    setTrabajando('etiquetas'); setError('')
    try {
      const { data } = await salidasApi.etiquetasAndreani(salida.id)
      window.open(URL.createObjectURL(new Blob([data], { type: 'application/pdf' })), '_blank')
    } catch (e) {
      let msg = 'No se pudieron bajar las etiquetas.'
      try { msg = JSON.parse(await e.response.data.text()).message || msg } catch { /* respuesta binaria */ }
      setError(msg)
    } finally { setTrabajando('') }
  }

  return (
    <div style={st.card}>
      <div style={st.fila}>
        <div>
          <p style={st.titulo}>{esAndreani ? 'Costo por Andreani (sin IVA)' : 'Costo de la salida'}</p>
          {editando ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="number" min="0" value={monto} onChange={e => setMonto(e.target.value)} style={st.input} placeholder="$" autoFocus />
              <Boton variante="primary" sm onClick={guardar} disabled={trabajando === 'costo'}>Guardar</Boton>
              <Boton variante="ghost" sm onClick={() => { setEditando(false); setMonto(salida.costo ?? '') }}>Cancelar</Boton>
            </div>
          ) : (
            <p style={st.monto}>
              {costo != null ? `$${fmtMonto(costo)}` : '—'}
              {costo != null && docs.length > 0 && <span style={st.porEntrega}> · ${fmtMonto(costo / docs.length)} por entrega ({docs.length})</span>}
              {esExterno && <button type="button" style={st.link} onClick={() => setEditando(true)}>{costo != null ? 'Cambiar' : 'Cargar lo que cobra'}</button>}
            </p>
          )}
        </div>

        {esAndreani && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Boton variante="primary" sm onClick={generar} disabled={!sinOrden || !!trabajando}>
              {trabajando === 'generar' ? 'Generando…' : sinOrden ? `📦 Generar ${sinOrden} envío(s) en Andreani` : '✓ Todos los envíos generados'}
            </Boton>
            <Boton variante="secondary" sm onClick={imprimir} disabled={!conOrden || !!trabajando}>
              {trabajando === 'etiquetas' ? 'Preparando…' : `🖨 Imprimir todas las etiquetas (${conOrden})`}
            </Boton>
          </div>
        )}
      </div>

      {esAndreani && docs.length > 0 && (
        <table style={st.tabla}>
          <tbody>
            {docs.map(d => {
              const e = d.entidad || {}
              const numero = d.entidad_tipo === 'nota_envio' ? `NE ${e.numero_ne || ''}` : `Re ${e.numero_presea || ''}`
              return (
                <tr key={d.id}>
                  <td style={{ ...st.td, fontFamily: 'monospace', fontWeight: 600 }}>{numero}</td>
                  <td style={st.td}>{e.andreani_tracking
                    ? <a href={`https://www.andreani.com/envio/${e.andreani_tracking}`} target="_blank" rel="noreferrer" style={st.tracking}>{e.andreani_tracking}</a>
                    : <span style={{ color: '#94a3b8' }}>sin orden</span>}</td>
                  <td style={{ ...st.td, color: '#64748b' }}>{e.andreani_trackings?.length > 1 ? `${e.andreani_trackings.length} bultos` : e.andreani_tracking ? '1 bulto' : ''}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {resultado && (
        <p style={st.nota}>
          ✓ {resultado.generados} generado(s){resultado.ya_tenian ? ` · ${resultado.ya_tenian} ya tenían orden` : ''}
          {resultado.errores?.length > 0 && <span style={{ color: '#92400e' }}> · {resultado.errores.length} con problema: {resultado.errores.map(x => `${x.numero}: ${x.motivo}`).join(' · ')}</span>}
        </p>
      )}
      {error && <p style={{ ...st.nota, color: '#991b1b' }}>{error}</p>}
    </div>
  )
}

const st = {
  card: { background: '#fff', borderRadius: 12, padding: 18, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,.06)' },
  fila: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  titulo: { fontSize: '0.8rem', color: '#64748b', fontWeight: 600, margin: '0 0 4px' },
  monto: { fontSize: '1.25rem', fontWeight: 800, color: '#1e293b', margin: 0, fontFamily: 'monospace' },
  porEntrega: { fontSize: '0.85rem', fontWeight: 600, color: '#64748b' },
  link: { marginLeft: 10, background: 'none', border: 'none', color: '#2563eb', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  input: { width: 140, padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.9rem' },
  tabla: { width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: '0.8rem' },
  td: { padding: '5px 8px', borderTop: '1px solid #f1f5f9' },
  tracking: { color: '#2563eb', fontFamily: 'monospace', textDecoration: 'none' },
  nota: { fontSize: '0.8rem', color: '#166534', margin: '10px 0 0' },
}
